import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useApolloClient, useMutation as useApolloMutation, useQuery } from '@apollo/client';
import { ADD_NEW_CAR_DRAFT, UPDATE_CAR } from '../../../services/carService/queries';
import { GET_ALL_PRODUCT_TYPE_DETAILS } from '../../../services/productTypeDetailsService/queries';
import { useEditingCarDraft } from '../../../hooks/useEditingCarDraft';
import {
  IUpdateCarMutation,
  IUpdateCarMutationVariables,
} from '../../../services/carService/__generated__/queries.generated';
import { carActions } from '../../../../store/carStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authStore } from '../../../../store/authStore';
import { useCreateLocationMutation } from '../../../services/locationServices/__generated__/queries.generated';
import { toastActions } from '../../../../store/toastStore';
import { ProductType } from '../../../../__generated__/graphql';
import {
  PRODUCT_TYPE_OPTIONS,
  ProductTypeOption,
  getProductTypeLabel,
} from '../../../constants/productTypeOptions';
import { AddressInput, LocationData } from '../../../components/addressInput';
import {
  GET_LOCATION_LEAN,
  GET_MY_LOCATIONS_V2_LEAN,
} from '../../../services/locationServices/queries';
import { GetMyLocationsV2LeanItem } from '../../../services/locationServices/getMyLocationsV2';
import LocationLoadMorePicker, {
  LocationLoadMorePickerMeta,
} from '../../../components/LocationLoadMorePicker';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  formatAddress,
  extractLocationAddressFieldsFromFormatted,
} from '../../../helpers/formatAddress';
import { locationHasOperatingHours } from '../../../helpers/operatingHours';
import { getSiteConfig } from '../../../constants/siteConfig';

const Container = styled.div`
  ${tw`max-w-lg mx-auto p-6 flex flex-col items-center justify-center`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-8`}
`;

const LocationSection = styled.div`
  ${tw`mb-6 w-full flex flex-col items-center`}
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2 w-full max-w-md`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4 w-full max-w-md`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2 w-full max-w-md`}
`;

const InputContainer = styled.div`
  ${tw`w-full max-w-md mx-auto mt-6 mb-4`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const StyledSelect = styled.select`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full text-white py-3 rounded-lg font-semibold transition-colors shadow-xs`}
  ${({ isActivated }) =>
    isActivated
      ? css`
          ${tw`bg-black`}

          &:hover {
            ${tw`bg-black-hover`}
          }
        `
      : tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full`}
`;

type AddCarSelectedLocation = GetMyLocationsV2LeanItem & {
  operatingHours?: Array<{
    id?: string | null;
    PickupLocationType?: string | null;
    alwaysAvailable?: boolean | null;
  } | null> | null;
};

export default function AddCarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { editingCar, ready: editingCarReady } = useEditingCarDraft();

  const editingCarType = (editingCar as { productTypeDetails?: { type?: ProductType } } | undefined)
    ?.productTypeDetails?.type as ProductType | undefined;

  const parseTypeParam = (value: string | null): ProductType | undefined => {
    if (!value) {
      return undefined;
    }
    const match = PRODUCT_TYPE_OPTIONS.find(option => option.value === (value as ProductType));
    return match?.value;
  };

  const siteConfig = getSiteConfig();
  const filteredTypeOptions = siteConfig.allowedProductTypes
    ? PRODUCT_TYPE_OPTIONS.filter(option => siteConfig.allowedProductTypes!.includes(option.value))
    : PRODUCT_TYPE_OPTIONS;

  const [selectedItemType, setSelectedItemType] = useState<ProductType>(() => {
    const urlType = parseTypeParam(searchParams.get('type'));
    // If URL has type parameter, use it; otherwise use editingCarType or site default
    return urlType ?? editingCarType ?? siteConfig.defaultProductType;
  });

  useEffect(() => {
    // Update selectedItemType if URL type changes
    const urlType = parseTypeParam(searchParams.get('type'));
    if (urlType && urlType !== selectedItemType) {
      setSelectedItemType(urlType);
    } else if (editingCarType && editingCarType !== selectedItemType && !urlType) {
      // Only update from editingCarType if URL doesn't have type parameter
      setSelectedItemType(editingCarType);
    }
  }, [searchParams, editingCarType, selectedItemType]);

  const selectedTypeLabel = useMemo(
    () => getProductTypeLabel(selectedItemType).toLowerCase(),
    [selectedItemType],
  );

  const apolloClient = useApolloClient();

  const { data: productTypeDetailsData, loading: productTypeDetailsLoading } = useQuery(
    GET_ALL_PRODUCT_TYPE_DETAILS,
  );

  const productTypeDetails = productTypeDetailsData?.productTypeDetails || [];

  const matchingProductType = useMemo(() => {
    if (!selectedItemType || productTypeDetails.length === 0) {
      return null;
    }
    const selectedType = String(selectedItemType).toUpperCase();
    return (
      productTypeDetails.find((ptd: { type?: ProductType | string; id?: string }) => {
        const ptdType = String(ptd.type || '').toUpperCase();
        return ptd.type === selectedItemType || ptdType === selectedType;
      }) ?? null
    );
  }, [productTypeDetails, selectedItemType]);
  const [pickerMeta, setPickerMeta] = useState<LocationLoadMorePickerMeta>({
    totalCount: 0,
    loading: true,
    error: false,
  });
  const locationTotalCount = pickerMeta.totalCount;
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<AddCarSelectedLocation | null>(null);
  const [currentAddress, setCurrentAddress] = useState('');
  const [newLocationAddress, setNewLocationAddress] = useState('');
  const [newLocationData, setNewLocationData] = useState<LocationData | null>(null);
  const [useNewLocation, setUseNewLocation] = useState(false);
  const [isSubmittingLocation, setIsSubmittingLocation] = useState(false);

  const goToNextStep = (locationId?: number) => {
    if (selectedItemType === ProductType.Car) {
      if (locationId) {
        navigate(`/cars/host/add-vin?locationId=${locationId}`);
      } else {
        navigate('/cars/host/add-vin');
      }
    } else {
      navigate('/cars/host/add-details');
    }
  };
  const handleItemTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedItemType(event.target.value as ProductType);
  };

  useEffect(() => {
    if (editingCar) {
      setCurrentAddress(editingCar.currentLocation || '');
    }
  }, [editingCar?.id]);

  // Lean draft mutation + no GET_ALL_CARS refetch (that query loads every car +
  // priceCalendar and made Next → VIN far slower than Flutter).
  const [addNewCar] = useApolloMutation(ADD_NEW_CAR_DRAFT);

  const [updateCar] = useApolloMutation<IUpdateCarMutation, IUpdateCarMutationVariables>(
    UPDATE_CAR,
  );

  const [createLocation, { loading: creatingLocation }] = useCreateLocationMutation({
    refetchQueries: [
      { query: GET_MY_LOCATIONS_V2_LEAN, variables: { input: { page: 1, limit: 20 } } },
    ],
  });

  const prefetchLocationDetails = (locationId: number) => {
    void apolloClient
      .query({
        query: GET_LOCATION_LEAN,
        variables: { id: locationId },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const loc = data?.location;
        if (!loc) {
          return;
        }
        setSelectedLocation(prev =>
          prev?.id === locationId
            ? {
                ...prev,
                ...loc,
                operatingHours: loc.operatingHours,
              }
            : prev,
        );
      })
      .catch(() => {
        // Soft-fail — Flutter continues when GetLocation fails.
      });
  };

  const handleLocationPickerChange = (
    locationId: number | null,
    location?: GetMyLocationsV2LeanItem,
  ) => {
    setUseNewLocation(false);
    setSelectedLocationId(locationId);
    if (location) {
      setSelectedLocation(location);
      setCurrentAddress(formatAddress(location));
      prefetchLocationDetails(location.id);
    } else if (locationId == null) {
      setSelectedLocation(null);
      setCurrentAddress('');
    }
  };

  const handleSelectCreateNew = () => {
    setUseNewLocation(true);
    setSelectedLocationId(null);
    setCurrentAddress(newLocationAddress);
  };

  const handleNewLocationSelect = (location: LocationData) => {
    setNewLocationData(location);
    setNewLocationAddress(location.formattedAddress || location.address);
    setCurrentAddress(location.formattedAddress || location.address);
  };

  const handleNewLocationChange = (address: string) => {
    setNewLocationAddress(address);
    if (locationTotalCount === 0 || useNewLocation) {
      setCurrentAddress(address);
    }
  };

  const onAddNewLocationisNeeded = () => {
    navigate('/cars/host/add-location');
  };

  const onSubmitLocation = async () => {
    if (!authStore.user?.id) {
      return;
    }

    // Wait for productTypeDetails to load if still loading
    if (productTypeDetailsLoading) {
      toast.error('Please wait for product type details to load');
      return;
    }

    setIsSubmittingLocation(true);
    try {
      let finalLocationId = selectedLocationId;

      // If user wants to create a new location
      if (useNewLocation) {
        if (!newLocationData) {
          toast.error('Please select an address from the suggestions');
          return;
        }

        try {
          const formattedAddr = newLocationData.formattedAddress || newLocationAddress;
          const parsed = extractLocationAddressFieldsFromFormatted(formattedAddr, {
            zipCode: newLocationData.zipCode,
          });

          const locationResponse = await createLocation({
            variables: {
              createLocationInput: {
                name: parsed.streetAddress,
                address: parsed.streetAddress,
                city: parsed.city,
                state: parsed.state,
                zipCode: parsed.zipCode || '',
                latitude: newLocationData.latitude,
                longitude: newLocationData.longitude,
                taxRate: 0,
              },
            },
          });

          const newLocation = locationResponse.data?.createLocation;
          if (!newLocation?.id) {
            toast.error('Failed to create location');
            return;
          }

          finalLocationId = newLocation.id;
          toast.success('Location created successfully');
        } catch (error: any) {
          toast.error(`Failed to create location: ${error.message}`);
          return;
        }
      }

      if (!finalLocationId) {
        toast.error('Please select or create a location');
        return;
      }

      if (
        !useNewLocation &&
        finalLocationId &&
        selectedLocation?.id === finalLocationId &&
        selectedLocation.operatingHours != null &&
        !locationHasOperatingHours(selectedLocation.operatingHours)
      ) {
        toastActions.showToastAlert(
          'Missing Operating Hours',
          `${selectedLocation?.address ?? 'This location'} is missing operating hours, press confirm to add them`,
          'add operating hours',
          () => {
            navigate(`/operating-hours/${finalLocationId}`);
          },
          () => {
            toast.info('add operating hours later');
          },
        );
        return;
      }

      try {
        if (!editingCar?.id && finalLocationId) {
          if (!selectedItemType) {
            toast.error('Please select an item type');
            return;
          }

          if (productTypeDetails.length === 0) {
            toast.error('Product type details not loaded yet. Please wait...');
            return;
          }

          if (!matchingProductType) {
            const availableTypes = productTypeDetails
              .map((ptd: { type?: string }) => ptd.type)
              .join(', ');
            toast.error(
              `Product type "${selectedItemType}" not found. Available types: ${availableTypes}`,
            );
            return;
          }

          const productTypeDetailsId = matchingProductType.id;

          let carLatitude: number | undefined;
          let carLongitude: number | undefined;

          if (useNewLocation && newLocationData) {
            carLatitude = newLocationData.latitude;
            carLongitude = newLocationData.longitude;
          } else {
            const coordsSource = selectedLocation?.id === finalLocationId ? selectedLocation : null;
            if (coordsSource?.latitude != null && coordsSource?.longitude != null) {
              carLatitude = Number(coordsSource.latitude);
              carLongitude = Number(coordsSource.longitude);
            }
          }

          const response = await addNewCar({
            variables: {
              carInputData: {
                ...(productTypeDetailsId && { productTypeDetailsId }),
                isCreated: false,
                ownerId: authStore.user?.id,
                locationId: finalLocationId,
                ...(carLatitude !== undefined &&
                  carLongitude !== undefined && {
                    latitude: carLatitude,
                    longitude: carLongitude,
                  }),
              },
            },
          });
          const newCar = response.data?.addNewCar;
          if (newCar?.id) {
            carActions.startEditing(newCar.id, {
              ...newCar,
              priceCalendar: null,
              discount: null,
            });
          }
        } else {
          if (!selectedItemType) {
            toast.error('Please select an item type');
            return;
          }

          if (productTypeDetails.length === 0) {
            toast.error('Product type details not loaded yet. Please wait...');
            return;
          }

          if (!matchingProductType) {
            const availableTypes = productTypeDetails
              .map((ptd: { type?: string }) => ptd.type)
              .join(', ');
            toast.error(
              `Product type "${selectedItemType}" not found. Available types: ${availableTypes}`,
            );
            return;
          }

          const productTypeDetailsId = matchingProductType.id;

          const locationChanged = editingCar && currentAddress !== editingCar.currentLocation;
          const productTypeChanged = editingCar?.productTypeDetails?.id !== productTypeDetailsId;

          // Always update productTypeDetailsId when user clicks Next
          const updateInput: any = {};

          // Update location if it changed
          if (locationChanged) {
            updateInput.currentLocation = currentAddress;
          }

          // Always update productTypeDetailsId to ensure it matches selectedItemType
          if (productTypeDetailsId) {
            updateInput.productTypeDetailsId = productTypeDetailsId;
          }

          const response = await updateCar({
            variables: {
              id: editingCar?.id!,
              updateCarInput: updateInput,
            },
          });
          const updatedCar = response.data?.updateCar;
          updatedCar?.id && carActions.startEditing(updatedCar.id, updatedCar);
        }
        goToNextStep(finalLocationId);
      } catch (error) {}
    } finally {
      setIsSubmittingLocation(false);
    }
  };
  if (!editingCarReady) {
    return <Container>Error</Container>;
  }

  if (!pickerMeta.loading && pickerMeta.error) {
    return <Container>Error loading locations</Container>;
  }

  if (!pickerMeta.loading && locationTotalCount === 0 && !useNewLocation) {
    return (
      <Container>
        <Title>No locations found</Title>
        <ButtonContainer>
          <NextButton isActivated onClick={() => navigate('/cars/host/add-location')}>
            Add Location
          </NextButton>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>List your {selectedTypeLabel}</Title>
      {!siteConfig.isSingleProductSite && (
        <LocationSection>
          <SectionTitle>Item Type</SectionTitle>
          <Description>Select what kind of item you want to list.</Description>
          <Label>Choose an item type</Label>
          <InputContainer>
            <StyledSelect value={selectedItemType} onChange={handleItemTypeChange}>
              {filteredTypeOptions.map((option: ProductTypeOption) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </InputContainer>
        </LocationSection>
      )}
      <LocationSection>
        <SectionTitle>Location</SectionTitle>
        <Description>Needs your location to ensure we operate where you are</Description>
        <Label>Where is your {selectedTypeLabel} located?</Label>
        <InputContainer>
          <LocationLoadMorePicker
            value={selectedLocationId}
            onChange={handleLocationPickerChange}
            allowCreateNew
            useNewLocation={useNewLocation}
            onSelectCreateNew={handleSelectCreateNew}
            selectedLocation={selectedLocation}
            onMetaChange={setPickerMeta}
            dataTestId="add-car-location-picker"
          />
          {useNewLocation && (
            <div style={{ marginTop: '16px' }}>
              <Label>Enter new location address</Label>
              <AddressInput
                value={newLocationAddress}
                placeholder="Enter an address..."
                onLocationSelect={handleNewLocationSelect}
                onLocationChange={handleNewLocationChange}
                isLoading={creatingLocation}
              />
            </div>
          )}
        </InputContainer>
      </LocationSection>
      <ButtonContainer>
        {isSubmittingLocation ? (
          <LoadingSpinner containerClassName="min-h-[48px] w-full" color="#EF4444" />
        ) : (
          <NextButton
            disabled={!currentAddress}
            isActivated={!!currentAddress}
            onClick={onSubmitLocation}
            data-testid="add-car-location-next"
          >
            Next
          </NextButton>
        )}
      </ButtonContainer>
    </Container>
  );
}
